import { createContext, useContext, type ReactNode } from 'react';

/**
 * What the screen around you is doing, for anything inside it that was also
 * thinking of arriving.
 *
 * **One arrival has one owner.** The screen that a finger opened is the arrival;
 * everything that turns up inside it before that arrival is over turns up *with*
 * it and has no entrance of its own. Without that rule the two runs multiply:
 * a screen fading in over 260ms and its data fading in over 180 from the 120th
 * millisecond means what was already visible at 0.45 drops to nought and starts
 * again, which is the jerk this exists to remove.
 *
 * The object is deliberately mutable and deliberately not state. It is read once
 * per mount — `Settling` decides its entrance from it and never looks again,
 * `Skeleton` reads it to know when it is allowed on screen — and a value that
 * caused a re-render would be a third thing arriving.
 */
export type Arrival = {
  /**
   * `performance.now()` at which this screen has finished arriving, and 0 for a
   * screen that never had a run: the first one of a session, one that arrived
   * while the webview was off screen, one for somebody who asked for less
   * movement.
   */
  endsAt: number;
  /** When a skeleton first went on screen under this arrival, or null. */
  skeletonAt: number | null;
  /**
   * Whether some `Settling` has already taken on seeing that skeleton off. A
   * screen may hold several — the roster holds two — and the departing copy is
   * drawn once, by the first of them to arrive.
   */
  seenOff: boolean;
};

/** A screen that is not going anywhere: the arrival is already over. */
export function settled(): Arrival {
  return { endsAt: 0, skeletonAt: null, seenOff: false };
}

/** A screen that has just begun a run of `ms`. */
export function arriving(ms: number): Arrival {
  return { endsAt: performance.now() + ms, skeletonAt: null, seenOff: false };
}

const Arrived = createContext<Arrival | null>(null);

export function Arriving({ arrival, children }: { arrival: Arrival; children: ReactNode }) {
  return <Arrived.Provider value={arrival}>{children}</Arrived.Provider>;
}

/** The arrival this is inside, or null outside a pushed screen and a tab. */
export function useArrival(): Arrival | null {
  return useContext(Arrived);
}
